import { DsmPageContainer } from '@/components';
import PageHeader from '@/components/PageHeader';
import { formatActivityDate, Pagination } from '@/dsm-components';
import usePermissions from '@/hooks/usePermissions';
import { type EquipmentManufacturer, useGetManufacturersQuery } from '@/services/equipmentManufacturers';
import {
  type DataTableColumn,
  type DataTableSortState,
  PalmettoButton,
  PalmettoDataTable,
  PalmettoSelect,
  PalmettoTag,
  PalmettoTextInput,
} from '@palmetto/dsm-components';
import { useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { EquipmentRecordStatus, ProgramType, UserPermissions } from 'types';

import { ProgramTypeTags } from '../shared/ProgramTypeTags';

import { EquipmentBreadcrumb } from './EquipmentBreadcrumb';
import {
  emptyValueLabel,
  enumParam,
  EquipmentRecordStatusLabels,
  EquipmentRecordStatusTagColors,
  pageParam,
  programTypeOptions,
  programTypeValues,
  statusOptions,
  statusValues,
} from './equipmentLabels';

const pageSize = 25;

export const EquipmentManufacturerList = () => {
  const navigate = useNavigate();
  const canEditAvl = usePermissions().includes(UserPermissions.canImportAvlData);

  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get('search') ?? '';
  // The API's status enum has no "both" value, so the filter is always one concrete status.
  const status =
    enumParam<EquipmentRecordStatus>(searchParams.get('status'), statusValues) || EquipmentRecordStatus.active;
  const programType = enumParam<ProgramType>(searchParams.get('programType'), programTypeValues);
  const page = pageParam(searchParams.get('page'));
  const sortBy = searchParams.get('sortBy') ?? '';
  const sortDir = searchParams.get('sortDir') === 'desc' ? 'desc' : 'asc';

  const { data, isLoading, isFetching, error, refetch } = useGetManufacturersQuery({
    ...(search ? { search } : {}),
    status,
    ...(programType ? { programType } : {}),
    ...(sortBy ? { sortBy, sortDir } : {}),
    limit: pageSize,
    offset: page * pageSize,
  });

  const items = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = new URLSearchParams(searchParams);
      if (e.target.value) next.set('search', e.target.value);
      else next.delete('search');
      next.delete('page');
      // Replace rather than push: one history entry per keystroke would make Back unusable.
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleStatusChange = useCallback(
    (details: { value: string[] }) => {
      const next = new URLSearchParams(searchParams);
      const value = details.value[0] ?? '';
      if (value) next.set('status', value);
      else next.delete('status');
      next.delete('page');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const handleProgramTypeChange = useCallback(
    (details: { value: string[] }) => {
      const next = new URLSearchParams(searchParams);
      const value = details.value[0] ?? '';
      if (value) next.set('programType', value);
      else next.delete('programType');
      next.delete('page');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const handlePageChange = useCallback(
    (nextPage: number) => {
      const next = new URLSearchParams(searchParams);
      next.set('page', String(nextPage));
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const sortState: DataTableSortState | null = useMemo(
    () => (sortBy ? { columnId: sortBy, direction: sortDir === 'desc' ? 'descending' : 'ascending' } : null),
    [sortBy, sortDir],
  );

  const handleSortChange = useCallback(
    (next: DataTableSortState | null) => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('page');

      if (next) {
        nextParams.set('sortBy', next.columnId);
        nextParams.set('sortDir', next.direction === 'descending' ? 'desc' : 'asc');
      } else {
        nextParams.delete('sortBy');
        nextParams.delete('sortDir');
      }

      setSearchParams(nextParams);
    },
    [searchParams, setSearchParams],
  );

  const handleNewManufacturer = useCallback(() => navigate('/admin/approved-vendors/manufacturers/new'), [navigate]);
  const handleOpenProductLines = useCallback(() => navigate('/admin/approved-vendors/product-lines'), [navigate]);

  const getRowId = useCallback((row: EquipmentManufacturer) => row.id, []);

  const columns: DataTableColumn<EquipmentManufacturer>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cellType: 'link',
        render: (row) => <Link to={`/admin/approved-vendors/manufacturers/${row.id}`}>{row.name}</Link>,
      },
      {
        id: 'programTypes',
        header: 'Program Types',
        width: '280px',
        cellType: 'tag',
        render: (row) => <ProgramTypeTags programTypes={row.programTypes} />,
      },
      {
        id: 'activeBrandCount',
        header: 'Active Brands',
        width: '140px',
        align: 'right',
        render: (row) => String(row.activeBrandCount),
      },
      {
        id: 'status',
        header: 'Status',
        width: '120px',
        sortable: true,
        cellType: 'tag',
        render: (row) => (
          <PalmettoTag
            label={EquipmentRecordStatusLabels[row.status] ?? row.status}
            color={EquipmentRecordStatusTagColors[row.status]}
          />
        ),
      },
      {
        id: 'modifiedAt',
        header: 'Last Modified',
        width: '280px',
        sortable: true,
        cellType: 'two-line',
        render: (row) => {
          const when = row.meta?.modifiedAt ? formatActivityDate(row.meta.modifiedAt) : emptyValueLabel;
          const who = row.modifiedByName ?? row.createdByName;
          return who ? `${when}\n${who}` : when;
        },
      },
    ],
    [],
  );

  return (
    <DsmPageContainer title="Equipment Manufacturers">
      <PageHeader title="Equipment Manufacturers" eyebrow={<EquipmentBreadcrumb />}>
        <div className="flex items-center gap-8">
          <PalmettoButton emphasis="secondary" onClick={handleOpenProductLines}>
            Energy Backup Product Lines
          </PalmettoButton>
          {canEditAvl && (
            <PalmettoButton emphasis="primary" onClick={handleNewManufacturer}>
              + New Manufacturer
            </PalmettoButton>
          )}
        </div>
      </PageHeader>

      <div className="mx-6 mb-4">
        <PalmettoTextInput
          id="manufacturer-search"
          label="Search"
          placeholder="Search name or alias"
          value={search}
          onChange={handleSearchChange}
        />
      </div>

      <div className="mx-6 mb-4 grid grid-cols-3 gap-4">
        <PalmettoSelect
          label="Status"
          items={statusOptions.map((opt) => ({ ...opt, selected: opt.value === status }))}
          onValueChange={handleStatusChange}
        />
        <PalmettoSelect
          label="Program Type"
          items={[
            { label: 'All', value: '', selected: !programType },
            ...programTypeOptions.map((opt) => ({ ...opt, selected: opt.value === programType })),
          ]}
          onValueChange={handleProgramTypeChange}
        />
      </div>

      <div className="mx-6 overflow-x-auto">
        <PalmettoDataTable
          columns={columns}
          data={items}
          getRowId={getRowId}
          sortState={sortState}
          onSortChange={handleSortChange}
          isLoading={isLoading || isFetching}
          error={error}
          onRetry={refetch}
          ariaLabel="Equipment manufacturers"
        />
      </div>

      {!error && totalPages > 1 && (
        <div className="mx-6 mt-4">
          <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
        </div>
      )}
    </DsmPageContainer>
  );
};
